/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import { useEffect } from 'react';
import { fg } from '@atlassian/jira-feature-gating';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import {
	createHook,
	createSelector,
	createStore,
	type Action,
	type BoundActions,
	type HookFunction,
} from '@atlassian/react-sweet-state';
import { localStorageProvider } from '@jira/platform__onboarding-core/src/utils/localStorageProvider.tsx';
import { TEAM_TYPE_SHORTHAND_LOCAL_STORAGE } from '@jira/platform__onboarding-core/src/constants.tsx';
import { mapUserSegmentationFullToShorthand } from '@jira/platform__onboarding-core/src/utils/map-user-segmentation-full-to-shorthand/index.tsx';
import { functionWithCondition } from '@jira/platform__utils-feature-flagging-utils/src/utils/function-with-condition/index.tsx';
import { actions } from './actions.tsx';
import type { State } from './types.tsx';

export const initialState: State = {
	isFetching: false,
	wasFetchedOnce: false,
	fetchError: null,
};

type ActionType = typeof actions;

const Store = createStore<State, ActionType>({
	initialState,
	actions,
	name: 'atlassian-account',
});

const loggedInEmailDomainSelector = createSelector(
	(state: State) => state.data,
	(accountInfo): string | null => {
		if (!accountInfo) {
			return null;
		}

		const [, loggedInEmailDomain] = accountInfo.email?.split('@') ?? [];
		return loggedInEmailDomain;
	},
);

/**
 * @deprecated Please migrate to Relay queries for fetching user data.
 * This hook uses react-sweet-state which is deprecated in Jira.
 *
 * For more details, see: https://hello.atlassian.net/wiki/spaces/JFP/pages/4901210770/ADR+Deprecate+atlassian+react-sweet-state+in+Jira
 *
 * Example GraphQL query to get user email:
 * ```
 * query userQuery {
 *   me {
 *     user {
 *       ... on AtlassianAccountUser {
 *         email
 *       }
 *     }
 *   }
 * }
 * ```
 */
export const useLoggedInEmailDomain: HookFunction<
	string | null,
	BoundActions<
		State,
		{
			readonly fetchAtlassianAccountData: ({ source }?: { source?: string }) => Action<State>;
		}
	>,
	void
> = createHook(Store, {
	selector: loggedInEmailDomainSelector,
});

/**
 * @deprecated Please migrate to Relay queries for fetching user data.
 * This hook uses react-sweet-state which is deprecated in Jira.
 *
 * For more details, see: https://hello.atlassian.net/wiki/spaces/JFP/pages/4901210770/ADR+Deprecate+atlassian+react-sweet-state+in+Jira
 *
 * Example GraphQL query to get user email:
 * ```graphql
 * query userQuery {
 *   me {
 *     user {
 *       ... on AtlassianAccountUser {
 *         email
 *       }
 *     }
 *   }
 * }
 * ```
 */
export const useUserEmailDomain = (
	isEligibleToCall = true,
): {
	data: string | null;
} => {
	const [loggedInEmailDomain, { fetchAtlassianAccountData }] = useLoggedInEmailDomain();

	useEffect(() => {
		if (isEligibleToCall && !loggedInEmailDomain) {
			fetchAtlassianAccountData();
		}
	}, [fetchAtlassianAccountData, isEligibleToCall, loggedInEmailDomain]);

	return { data: loggedInEmailDomain };
};

/**
 * @deprecated Please migrate to Relay queries for fetching user data.
 * This hook uses react-sweet-state which is deprecated in Jira.
 *
 * For more details, see: https://hello.atlassian.net/wiki/spaces/JFP/pages/4901210770/ADR+Deprecate+atlassian+react-sweet-state+in+Jira
 *
 * * Example GraphQL query to get user email:
 * ```graphql
 * query userQuery {
 *   me {
 *     user {
 *       ... on AtlassianAccountUser {
 *         email
 *       }
 *     }
 *   }
 * }
 * ```
 */
export const useUserEmailDomainWithMentionFg: (isEligibleToCall?: boolean) => {
	data: string | null;
} = functionWithCondition(
	() => fg('jira_invites_auto_tag_new_user_in_mentions_fg'),
	useUserEmailDomain,
	() => ({ data: null }),
);

/**
 * @deprecated Please migrate to Relay queries for fetching user data.
 * This hook uses react-sweet-state which is deprecated in Jira.
 *
 * For more details, see: https://hello.atlassian.net/wiki/spaces/JFP/pages/4901210770/ADR+Deprecate+atlassian+react-sweet-state+in+Jira
 *
 * Example GraphQL query to get user email and team type:
 * ```graphql
 * query userQuery {
 *   me {
 *     user {
 *       ... on AtlassianAccountUser {
 *         email
 *         extendedProfile {
 *           teamType
 *         }
 *       }
 *     }
 *   }
 * }
 * ```
 */
export const useAtlassianAccount: HookFunction<
	State,
	BoundActions<
		State,
		{
			readonly fetchAtlassianAccountData: ({ source }?: { source?: string }) => Action<State>;
		}
	>,
	void
> = createHook(Store);

/**
 * @deprecated Please migrate to Relay queries for fetching user data.
 * This hook uses react-sweet-state which is deprecated in Jira.
 *
 * For more details, see: https://hello.atlassian.net/wiki/spaces/JFP/pages/4901210770/ADR+Deprecate+atlassian+react-sweet-state+in+Jira
 *
 * Example GraphQL query to get user team type:
 * ```graphql
 * query userQuery {
 *   me {
 *     user {
 *       ... on AtlassianAccountUser {
 *         extendedProfile {
 *           teamType
 *         }
 *       }
 *     }
 *   }
 * }
 * ```
 *
 * A localStorageProvider backed hook for fetching the user's shorthand team type
 */
export const useTeamTypeShortHand = ({
	shouldFetch,
}: {
	shouldFetch: boolean;
}): {
	isReady: boolean;
	// oxlint-disable-next-line @typescript-eslint/no-explicit-any
	teamType: any;
	fetchError: Error | null;
} => {
	const [
		{ data: atlassianAccountData, isFetching, wasFetchedOnce, fetchError },
		{ fetchAtlassianAccountData },
	] = useAtlassianAccount();

	const localStorageTeamTypeShorthand = localStorageProvider.get(TEAM_TYPE_SHORTHAND_LOCAL_STORAGE);

	useEffect(() => {
		if (shouldFetch && !localStorageTeamTypeShorthand && !isFetching && !wasFetchedOnce) {
			fetchAtlassianAccountData({ source: 'useTeamTypeShortHand' });
		}
	}, [
		fetchAtlassianAccountData,
		isFetching,
		localStorageTeamTypeShorthand,
		shouldFetch,
		wasFetchedOnce,
	]);

	if (localStorageTeamTypeShorthand) {
		return {
			isReady: true,
			teamType: localStorageTeamTypeShorthand,
			fetchError,
		};
	}

	if (!shouldFetch) {
		return {
			isReady: true,
			teamType: mapUserSegmentationFullToShorthand(atlassianAccountData?.extendedProfile.teamType),
			fetchError,
		};
	}

	if (atlassianAccountData?.extendedProfile.teamType && !localStorageTeamTypeShorthand) {
		localStorageProvider.set(
			TEAM_TYPE_SHORTHAND_LOCAL_STORAGE,
			mapUserSegmentationFullToShorthand(atlassianAccountData.extendedProfile.teamType),
		);
	}

	return {
		isReady: !isFetching && wasFetchedOnce,
		teamType: mapUserSegmentationFullToShorthand(atlassianAccountData?.extendedProfile.teamType),
		fetchError,
	};
};
